import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Button, Menu } from '~/components';
import { bypassBlocker } from '~/constants/router';
import { SearchParamKeys } from '~/constants/url';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { persistFormDataInSession } from '~/utils/form';
import { qs } from '~/utils/searchParams';
import { uuidv4 } from '~/utils/string';

import { useFormWithLinesContext } from '../../FormLines.context';
import { useSectionContext } from '../../Section/Section.context';
import { useLineContext } from '../../SectionLines/SectionLines.context';

type Props = {
  className?: string;
};

export const LineActions = ({ className }: Props) => {
  const {
    clearErrors,
    formState: { isSubmitted },
    getValues,
    setValue,
    trigger,
    watch,
  } = useFormWithLinesContext();
  const { isProductLine, line, lineFieldName, lineIndex } = useLineContext();

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'lines']);

  const discountTypeFieldName = `${lineFieldName}.discountType` as const;
  const discountValueFieldName = `${lineFieldName}.discountValue` as const;
  const watchDiscountType = watch(discountTypeFieldName);

  const { linesFieldArray, section, sectionIndex, sectionsFieldArray } = useSectionContext();
  const { fields: sections, remove: removeSection } = sectionsFieldArray;
  const { fields: lines, insert: insertLine, remove: removeLine } = linesFieldArray;

  /**
   * Remove the line, or the whole section if it is the last line (unless it's the default section)
   */
  const remove = useCallback(
    (lineIndex: number) => {
      const isLastLine = lines.length === 1;
      const isLastSection = sections.length === 1;

      if (isLastLine && section.isPhotoAlbum) {
        removeSection(sectionIndex);
      } else {
        removeLine(lineIndex);
      }

      // Retrigger the section validation as removing a line doesn't do this automatically
      if (isSubmitted && isLastLine && isLastSection) {
        trigger('sections');
      }
    },
    [isSubmitted, lines.length, removeLine, removeSection, section.isPhotoAlbum, sectionIndex, sections.length, trigger],
  );

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Menu.Root>
        <Menu.Trigger render={<Button aria-label={t('common:actions.openMenu')} className={className} icon="MoreVert" />} />

        <Menu.List>
          <Menu.Item icon="Trash" onClick={() => remove(lineIndex)}>
            {t('common:actions.delete')}
          </Menu.Item>

          <Menu.Item icon="EditDocument" onClick={() => insertLine(lineIndex + 1, { ...getValues(lineFieldName), uuid: uuidv4() })}>
            {t('lines:actions.duplicate')}
          </Menu.Item>

          {line.type === 'entry' && (
            <Menu.Item
              icon="Redeem"
              onClick={() => {
                if (watchDiscountType === null) {
                  setValue(discountTypeFieldName, 'percentage');
                  setValue(discountValueFieldName, null);
                } else {
                  setValue(discountTypeFieldName, null);
                  setValue(discountValueFieldName, null);
                  if (isSubmitted) clearErrors(discountValueFieldName);
                }
              }}
            >
              {watchDiscountType === null ? t('lines:actions.applyDiscount') : t('lines:actions.removeDiscount')}
            </Menu.Item>
          )}

          {isProductLine && (
            <Menu.Item
              icon="Package"
              onClick={() => {
                // Reduntant checks to make TypeScript happy
                if (line.type !== 'entry' || line.productId === null) return;

                persistFormDataInSession(window.location.pathname, getValues());

                navigate(
                  {
                    pathname: r(routes.editProduct, { productId: line.productId }),
                    search: qs({ [SearchParamKeys.REDIRECT_PATH]: window.location.pathname }),
                  },
                  { state: bypassBlocker },
                );
              }}
            >
              {t('lines:actions.editProduct')}
            </Menu.Item>
          )}
        </Menu.List>
      </Menu.Root>
    </div>
  );
};
